import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AudioButton from "../AudioButton";
import Confetti from "../Confetti";
import { speak, speakPhoneme, speakWord, preload } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";

const VOWELS = "aeiou";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function BridgeBuilderGame({ level, onFinish }) {
  const words = level.words;
  const [wi, setWi] = useState(0);
  const [placed, setPlaced] = useState([]); // letters placed in order
  const [mistakes, setMistakes] = useState(0);
  const [crossed, setCrossed] = useState(false);
  const [burst, setBurst] = useState(false);
  const [shakeLetter, setShakeLetter] = useState(null);

  const word = words[wi];
  const letters = useMemo(() => word.split(""), [word]);
  const tiles = useMemo(() => shuffle(letters.map((l, i) => ({ l, id: `${l}-${i}` }))), [word]);

  useEffect(() => {
    preload([{ text: word, kind: "word" }, ...letters.map((l) => ({ text: l, kind: "phoneme" }))]);
    const t = setTimeout(() => speak("Build the word! Tap the letters in order."), 400);
    return () => clearTimeout(t);
  }, [wi]); // eslint-disable-line

  const nextIndex = placed.length;

  const tapTile = (tile) => {
    if (crossed) return;
    const expected = letters[nextIndex];
    if (tile.l === expected) {
      sfx.tap();
      speakPhoneme(tile.l);
      const np = [...placed, tile.id];
      setPlaced(np);
      if (np.length === letters.length) {
        // word complete -> blend
        setTimeout(() => {
          sfx.correct();
          speak(`${letters.join("... ")}...`);
          setTimeout(() => speakWord(word), 1200);
          setBurst(true);
          setCrossed(true);
          setTimeout(() => setBurst(false), 1500);
          setTimeout(advance, 2600);
        }, 350);
      }
    } else {
      sfx.wrong();
      setShakeLetter(tile.id);
      setMistakes((m) => m + 1);
      speak("Hmm, try the next sound!");
      setTimeout(() => setShakeLetter(null), 500);
    }
  };

  const advance = () => {
    if (wi + 1 >= words.length) {
      const stars = mistakes === 0 ? 3 : mistakes <= 2 ? 2 : 1;
      onFinish({ stars, accuracy: Math.max(0, 1 - mistakes / (words.length * 2)) });
    } else {
      setWi((i) => i + 1);
      setPlaced([]);
      setCrossed(false);
    }
  };

  const placedTileIds = new Set(placed);

  return (
    <div className="relative flex flex-col items-center px-4 pt-3 pb-8 min-h-full no-select">
      <Confetti show={burst} count={50} />

      <div className="flex items-center gap-3 bg-white/95 rounded-3xl border-4 border-amber-300 shadow-lg px-4 py-3 mt-2 max-w-lg">
        <AudioButton text="Build the word! Tap the letters in order." testId="prompt-audio" size={56} />
        <p className="font-display text-lg sm:text-xl font-bold text-amber-900" data-testid="game-prompt">
          Build the bridge! Tap letters in order.
        </p>
      </div>

      {/* Chasm + bridge slots */}
      <div className="relative w-full max-w-xl mt-8 mb-4">
        <div className="flex items-end justify-center gap-3">
          {/* start cliff */}
          <div className="w-14 h-20 rounded-t-xl bg-green-600 shadow-[inset_0_6px_0_#16A34A] flex items-start justify-center pt-1">
            <motion.span
              className="text-3xl"
              animate={crossed ? { x: [0, 60, 130], y: [0, -30, 0] } : {}}
              transition={{ duration: 1.6 }}
              data-testid="bridge-walker"
            >
              {crossed ? "" : "🐥"}
            </motion.span>
          </div>

          {/* slots */}
          {letters.map((l, i) => {
            const filled = i < placed.length;
            return (
              <div key={i} className="flex flex-col items-center">
                <motion.div
                  data-testid={`bridge-slot-${i}`}
                  animate={{ y: filled ? 0 : [0, -6, 0] }}
                  transition={filled ? {} : { repeat: Infinity, duration: 2, delay: i * 0.2 }}
                  className={`flex items-center justify-center rounded-2xl border-4 font-display text-3xl font-black ${
                    filled
                      ? VOWELS.includes(l)
                        ? "bg-red-500 text-white border-red-200"
                        : "bg-blue-500 text-white border-blue-200"
                      : "bg-white/70 text-transparent border-dashed border-amber-300"
                  }`}
                  style={{ width: 64, height: 64 }}
                >
                  {filled ? l : "?"}
                </motion.div>
                {/* plank */}
                <div className={`w-16 h-2 rounded-full mt-1 ${filled ? "bg-amber-700" : "bg-amber-200"}`} />
              </div>
            );
          })}

          {/* end cliff */}
          <div className="w-14 h-20 rounded-t-xl bg-green-600 shadow-[inset_0_6px_0_#16A34A] flex items-start justify-center pt-1">
            <motion.span
              className="text-3xl"
              animate={crossed ? { scale: [0, 1.3, 1] } : {}}
              transition={{ delay: 1.4 }}
            >
              {crossed ? "🐥" : "🏁"}
            </motion.span>
          </div>
        </div>
      </div>

      {/* Letter tray */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-4" data-testid="letter-tray">
        <AnimatePresence>
          {tiles
            .filter((t) => !placedTileIds.has(t.id))
            .map((t) => (
              <motion.button
                key={t.id}
                data-testid={`letter-tile-${t.l}`}
                onClick={() => tapTile(t)}
                layout
                initial={{ scale: 0 }}
                animate={{ scale: 1, x: shakeLetter === t.id ? [0, -8, 8, 0] : 0 }}
                exit={{ scale: 0, opacity: 0 }}
                whileTap={{ scale: 0.88 }}
                className={`pop-btn flex items-center justify-center rounded-2xl font-display text-4xl font-black text-white border-4 border-white active:shadow-none ${
                  VOWELS.includes(t.l) ? "bg-red-500 shadow-[0_6px_0_#B91C1C]" : "bg-blue-500 shadow-[0_6px_0_#1D4ED8]"
                }`}
                style={{ width: 72, height: 72 }}
              >
                {t.l}
              </motion.button>
            ))}
        </AnimatePresence>
      </div>

      <div className="flex gap-2 mt-7">
        {words.map((_, i) => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < wi ? "bg-green-500" : i === wi ? "bg-amber-400 animate-pulse" : "bg-slate-300"}`} />
        ))}
      </div>
    </div>
  );
}
